/**
 * Mandatory cooperative saving (simpanan wajib) on a person's Pay & bank tab: the amount
 * withheld each period and what their approved payslips have withheld so far.
 */
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { get } from '../lib/api.ts';
import { dateRange, idr, idrCompact } from '../lib/format.ts';
import { Callout, Panel, Skeleton } from './ui.tsx';

interface Savings {
  on: boolean;
  perPeriod: string | null;
  minWorkingDays: number;
  voluntaryPerPeriod: string | null;
  withheld: { total: string; periods: number; since: string | null; last: { amount: string; periodFrom: string; periodTo: string } | null };
  /** Null unless the viewer is the member or cooperative staff. */
  balance: string | null;
}

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

export function PersonSavings({ employeeId, name, self }: { employeeId: string; name: string; self: boolean }) {
  const q = useQuery({ queryKey: ['person-savings', employeeId], queryFn: () => get<Savings>(`/api/payroll/employees/${employeeId}/savings`) });
  const s = q.data;
  const total = idrCompact(s?.withheld.total);
  const per = idrCompact(s?.perPeriod);
  return (
    <Panel
      eyebrow="Cooperative"
      title="Mandatory savings"
      action={
        self && s?.balance !== null ? (
          <Link to="/cooperative" className="btn btn--ghost btn--sm">
            Open ledger
          </Link>
        ) : undefined
      }
    >
      {q.isError ? (
        <p className="faint">Savings could not be loaded.</p>
      ) : !s ? (
        <Skeleton h={160} />
      ) : (
        <div className="stack">
          {!s.on ? <Callout tone="info">Not withheld at the moment: mandatory saving is switched off for {self ? 'you' : name}.</Callout> : null}
          <div className="ledger" style={{ ['--cols' as string]: 2 }}>
            <div className="ledger__cell ledger__cell--accent">
              <div className="ledger__value">
                {total.value}
                <small>{total.unit}</small>
              </div>
              <div className="ledger__label eyebrow">Withheld so far</div>
              <div className="ledger__delta num">
                {s.withheld.periods ? `${idr(s.withheld.total)} · ${s.withheld.periods} payslip${s.withheld.periods === 1 ? '' : 's'} since ${MONTH.format(new Date(`${s.withheld.since}T00:00:00Z`))}` : 'Nothing on an approved payslip yet'}
              </div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value">
                {s.on && s.perPeriod ? (
                  <>
                    {per.value}
                    <small>{per.unit}</small>
                  </>
                ) : (
                  '—'
                )}
              </div>
              <div className="ledger__label eyebrow">Each pay period</div>
              <div className="ledger__delta">{s.on && s.perPeriod ? `${idr(s.perPeriod)}, with ${s.minWorkingDays}+ days worked` : s.on ? 'No company amount set' : 'Switched off'}</div>
            </div>
          </div>
          <dl className="dl">
            <dt>Last withheld</dt>
            <dd>
              {s.withheld.last ? (
                <>
                  <span className="num">{idr(s.withheld.last.amount)}</span> <span className="faint">· {dateRange(s.withheld.last.periodFrom, s.withheld.last.periodTo)}</span>
                </>
              ) : (
                '—'
              )}
            </dd>
            {s.voluntaryPerPeriod ? (
              <>
                <dt>Voluntary saving</dt>
                <dd className="num">{idr(s.voluntaryPerPeriod)} per period</dd>
              </>
            ) : null}
            {s.balance !== null ? (
              <>
                <dt>Cooperative balance</dt>
                <dd>
                  <span className="num">{idr(s.balance)}</span> <span className="faint">· all savings with interest, less withdrawals</span>
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      )}
    </Panel>
  );
}
